import React, {useState, useEffect} from "react";
import {useAuth} from "../../AuthContext";
import {useNotifications} from "../../components/NotificationSystem";
import "../../styles/env.css";

const Settings: React.FC = () => {
  const {user, updateSettings, deleteAccount, error, clearError} = useAuth();
  const {addNotification} = useNotifications();
  const [open, setOpen] = useState({
    account: true,
  });

  // Form state
  const [formData, setFormData] = useState({
    displayName: "",
    bio: "",
    gameReminders: true,
    leagueUpdates: true,
  });

  // Loading states
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Initialize form with user data
  useEffect(() => {
    if (user) {
      setFormData({
        displayName: user.displayName || "",
        bio: user.bio || "",
        gameReminders:
          user.preferences?.emailNotifications?.gameReminders ?? true,
        leagueUpdates:
          user.preferences?.emailNotifications?.leagueUpdates ?? true,
      });
    }
  }, [user]);

  const toggle = (key: keyof typeof open) => {
    setOpen((prev) => ({...prev, [key]: !prev[key]}));
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    if (!user) return;

    // Validate form data
    if (!formData.displayName.trim()) {
      addNotification({
        type: "warning",
        title: "Validation Error",
        message: "Display name cannot be empty.",
        duration: 4000,
      });
      return;
    }

    try {
      setIsSaving(true);
      clearError();

      await updateSettings({
        displayName: formData.displayName.trim(),
        bio: formData.bio.trim(),
        preferences: {
          emailNotifications: {
            gameReminders: formData.gameReminders,
            leagueUpdates: formData.leagueUpdates,
          },
        },
      });

      // Show success notification
      addNotification({
        type: "success",
        title: "Settings Saved",
        message: "Your settings have been updated successfully.",
        duration: 4000,
      });
    } catch (err: any) {
      // Failed to save settings - handled by notification system
      addNotification({
        type: "error",
        title: "Save Failed",
        message: err.message || "Failed to save settings. Please try again.",
        duration: 6000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!user) return;

    try {
      setIsDeleting(true);
      clearError();

      // Show info notification about the process
      addNotification({
        type: "info",
        title: "Deleting Account",
        message:
          "Please wait while we delete your account and all associated data...",
        duration: 2000,
      });

      await deleteAccount();

      // Show success notification before redirect
      addNotification({
        type: "success",
        title: "Account Deleted",
        message: "Your account has been permanently deleted.",
        duration: 3000,
      });

      // User will be automatically signed out and redirected
    } catch (err: any) {
      // Failed to delete account - handled by notification system
      setShowDeleteConfirm(false);

      addNotification({
        type: "error",
        title: "Delete Failed",
        message: err.message || "Failed to delete account. Please try again.",
        duration: 6000,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  if (!user) {
    return (
      <div className="env-page-container">
        <h2 className="env-page-title">Settings</h2>
        <div className="env-settings-content">
          <p>Please log in to access your settings.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="env-page-container">
      <h2 className="env-page-title">Settings</h2>

      {/* Error Display */}
      {error && <div className="env-error-message">{error}</div>}

      {/* ACCOUNT SETTINGS */}
      <section className="env-settings-section">
        <button
          className="env-settings-header"
          onClick={() => toggle("account")}
        >
          ACCOUNT SETTINGS
          <span className="env-settings-toggle">
            {open.account ? "▾" : "▸"}
          </span>
        </button>
        {open.account && (
          <div className="env-settings-content">
            {/* Profile Information */}
            <div className="env-settings-card">
              <div className="env-settings-card-header">
                PROFILE INFORMATION
              </div>
              <div className="env-settings-form">
                <div className="env-settings-field">
                  <label className="env-settings-label">Display Name</label>
                  <input
                    type="text"
                    value={formData.displayName}
                    onChange={(e) =>
                      handleInputChange("displayName", e.target.value)
                    }
                    placeholder="Enter your display name"
                    className="env-input"
                  />
                </div>
                <div className="env-settings-field">
                  <label className="env-settings-label">Bio</label>
                  <textarea
                    value={formData.bio}
                    onChange={(e) => handleInputChange("bio", e.target.value)}
                    placeholder="Tell us about yourself"
                    className="env-textarea"
                    rows={3}
                  />
                </div>
              </div>
            </div>

            {/* Email Preferences */}
            <div className="env-settings-card">
              <div className="env-settings-card-header">EMAIL PREFERENCES</div>
              <div className="env-settings-field">
                <div className="env-settings-label">Game reminders</div>
                <div className="env-settings-radio-group">
                  <label className="env-radio-label">
                    <input
                      type="radio"
                      name="game-reminders"
                      checked={formData.gameReminders}
                      onChange={() => handleInputChange("gameReminders", true)}
                    />
                    Yes
                  </label>
                  <label className="env-radio-label">
                    <input
                      type="radio"
                      name="game-reminders"
                      checked={!formData.gameReminders}
                      onChange={() => handleInputChange("gameReminders", false)}
                    />
                    No
                  </label>
                </div>
                <div className="env-settings-description">
                  You will receive an email reminding you about upcoming games
                </div>
              </div>
              <div className="env-settings-field">
                <div className="env-settings-label">League updates</div>
                <div className="env-settings-radio-group">
                  <label className="env-radio-label">
                    <input
                      type="radio"
                      name="league-updates"
                      checked={formData.leagueUpdates}
                      onChange={() => handleInputChange("leagueUpdates", true)}
                    />
                    Yes
                  </label>
                  <label className="env-radio-label">
                    <input
                      type="radio"
                      name="league-updates"
                      checked={!formData.leagueUpdates}
                      onChange={() => handleInputChange("leagueUpdates", false)}
                    />
                    No
                  </label>
                </div>
                <div className="env-settings-description">
                  You will receive emails about league announcements and updates
                </div>
              </div>
            </div>

            {/* Save Button */}
            <div className="env-settings-card">
              <button
                className="env-btn-primary env-btn-full"
                onClick={handleSave}
                disabled={isSaving}
                style={{marginBottom: "20px"}}
              >
                {isSaving ? "Saving..." : "Save Changes"}
              </button>
            </div>

            {/* Danger Zone */}
            <div className="env-settings-card">
              <div className="env-settings-card-header-danger">DANGER ZONE</div>
              <button
                className="btn btn-destructive btn-full"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Permanently delete your account"}
              </button>
            </div>
          </div>
        )}
      </section>

      {/* Delete Account Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="env-blanket-overlay">
          <div className="env-modal">
            <div className="env-modal-header">
              <h3>Delete Account</h3>
            </div>
            <div className="env-modal-content">
              <div className="env-modal-body">
                <p>
                  Are you sure you want to permanently delete your account? This
                  action cannot be undone.
                </p>
                <p style={{fontSize: "14px", color: "#666", marginTop: "16px"}}>
                  This will delete all your data including characters, session
                  history, and preferences.
                </p>
              </div>
            </div>
            <div className="env-modal-actions">
              <button
                className="btn btn-secondary btn-full"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                className="btn btn-destructive btn-full"
                onClick={handleDeleteAccount}
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Delete Account"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
